import {
  Button,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import React, { useEffect, useState } from "react";
import { Header } from "../../graphql/schema";
import Img from "../common/image/Img";
import { Btn } from "../common/input/Btn";
import { useLocation, useNavigate } from "react-router-dom";
import { DismissFilled, NavigationFilled } from "@fluentui/react-icons";
import { LanguageSwitcher } from "./LanguageSwitcher";

const useStyles = makeStyles({
  nav: {
    height: "60px",
    width: "100%",
    backgroundColor: tokens.colorBrandBackground,
    position: "fixed",
    top: 0,
    zIndex: 4,
  },
  navContent: {
    ...shorthands.padding(0, tokens.spacingVerticalXL),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    "@media screen and (min-width: 700px)": {
      ...shorthands.padding(0, tokens.spacingVerticalXXXL),
    },
  },
  image: {
    height: "50px",
    cursor: "pointer",
    "@media screen and (min-width: 700px)": {
      height: "50px",
    },
  },

  // Desktop
  desktopLinks: {
    display: "none",
    "@media screen and (min-width: 700px)": {
      display: "flex",
      alignItems: "center",
      columnGap: tokens.spacingHorizontalM,
    },
  },
  loginButton: {
    marginLeft: tokens.spacingHorizontalXL,
    ...shorthands.borderRadius("20px"),
    backgroundColor: "#ffffff",
    color: tokens.colorBrandBackground,
    ":hover": {
      color: tokens.colorBrandBackground,
      backgroundColor: tokens.colorBrandBackground2Hover,
    },
  },

  // Mobile
  mobileToggle: {
    display: "flex",
    height: "50px",
    alignItems: "center",
    columnGap: tokens.spacingHorizontalS,
    "@media screen and (min-width: 700px)": {
      display: "none",
    },
  },
  mobileLanguageSwitcher: {
    flexShrink: 0,
  },

  mobileMenu: {
    display: "none",
    "@media screen and (max-width: 699px)": {
      position: "fixed",
      width: "100%",
      top: "60px",
      left: 0,
      right: 0,
      zIndex: 3,
      backgroundColor: tokens.colorNeutralForegroundInverted,
      boxShadow: tokens.shadow16,
      display: "flex",
      flexDirection: "column",
      rowGap: tokens.spacingVerticalM,
      ...shorthands.padding(
        tokens.spacingVerticalL,
        tokens.spacingHorizontalXL
      ),
    },
  },
  mobileMenuHidden: {
    display: "none !important",
  },

  mobileMenuButton: {
    justifyContent: "flex-start",
    width: "100%",
    color: tokens.colorBrandBackground,
    fontSize: tokens.fontSizeBase400,
  },
  colorWhite: {
    color: "#FFFFFF",
  },
});

type NavProps = {
  data: Header;
  contentClassName?: string;
};
export const Nav = ({ data, contentClassName }: NavProps) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [navigationExpanded, setNavigationExpanded] = useState(false);
  const loginLink = data.loginLink;

  useEffect(() => {
    if (navigationExpanded) {
      setNavigationExpanded(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  const toggleNavigation = () => {
    setNavigationExpanded(!navigationExpanded);
  };

  const goLogin = () => {
    if (loginLink && loginLink.startsWith("https")) {
      window.location.href = loginLink;
    } else if (loginLink) {
      navigate(loginLink);
    }
  };

  const goAndCollapse = (pathOrFunc: () => void) => {
    pathOrFunc();
    setNavigationExpanded(false);
  };

  return (
    <nav className={classes.nav}>
      <div className={mergeClasses(classes.navContent, contentClassName ?? "")}>
        <Img
          onClick={() => navigate("/")}
          className={classes.image}
          alt={
            data?.logo?.alternativeText ?? ""
          }
          src={data?.logo?.url}
        />

        {/* Desktop links */}
        <div className={classes.desktopLinks}>
          <Btn appearance="primary" onClick={() => navigate("/order")}>
            {data?.getRobustText ?? ""}
          </Btn>
          <Btn appearance="primary" onClick={() => navigate("/about")}>
            {data?.aboutUsText ?? ""}
          </Btn>
          <Btn appearance="primary" onClick={() => navigate("/faq")}>
            {data?.faqText ?? ""}
          </Btn>
          <Button
            className={classes.loginButton}
            appearance="secondary"
            onClick={goLogin}
          >
            {data?.loginText}
          </Button>
          <LanguageSwitcher />
        </div>

        {/* Mobile hamburger*/}
        <div className={classes.mobileToggle}>
          <LanguageSwitcher className={classes.mobileLanguageSwitcher} />
          <Button
            className={classes.colorWhite}
            icon={navigationExpanded ? <DismissFilled /> : <NavigationFilled />}
            size="large"
            appearance="transparent"
            onClick={toggleNavigation}
          />
        </div>
      </div>

      {/* Mobile dropdown menu */}
      <div
        className={mergeClasses(
          classes.mobileMenu,
          !navigationExpanded && classes.mobileMenuHidden
        )}
      >
        <Button
          appearance="transparent"
          className={classes.mobileMenuButton}
          onClick={() => goAndCollapse(() => navigate("/order"))}
        >
          {data?.getRobustText ?? ""}
        </Button>
        <Button
          appearance="transparent"
          className={classes.mobileMenuButton}
          onClick={() => goAndCollapse(() => navigate("/about"))}
        >
          {data?.aboutUsText ?? ""}
        </Button>
        <Button
          appearance="transparent"
          className={classes.mobileMenuButton}
          onClick={() => goAndCollapse(() => navigate("/faq"))}
        >
          {data?.faqText ?? ""}
        </Button>
        <Button
          appearance="transparent"
          className={classes.mobileMenuButton}
          onClick={() => goAndCollapse(goLogin)}
        >
          {data?.loginText}
        </Button>
      </div>
    </nav>
  );
};
